import * as React from "react";

import { cn } from "../lib/utils";

// The auto-growing behavior comes from CSS `field-sizing: content`
// (`field-sizing-content` below). Firefox doesn't support it, so there we
// replicate it by measuring scrollHeight. Any `max-height` on the textarea
// still clamps the fallback, matching the native behavior.
const supportsFieldSizing =
  typeof CSS !== "undefined" && CSS.supports("field-sizing", "content");

function resizeToContent(el: HTMLTextAreaElement) {
  el.style.height = "auto";
  // scrollHeight excludes borders, but border-box height includes them.
  const borders = el.offsetHeight - el.clientHeight;
  el.style.height = `${el.scrollHeight + borders}px`;
}

/**
 * The growing half of `Textarea`, for a textarea that can't be one.
 *
 * A surface that draws its own field — one with no border or box, say — still
 * needs a long value to grow rather than scroll inside a slot. The CSS half of
 * that is a single class anyone can copy; this is the half that is easy to
 * forget and then impossible to notice, because it only does anything in a
 * browser without `field-sizing`. Sharing it keeps that from being rediscovered
 * one surface at a time.
 *
 * Returns the `ref` and `onInput` to spread onto the element, with the
 * caller's own versions of both chained.
 */
export function useAutoGrowTextarea({
  ref,
  onInput,
}: {
  ref?: React.Ref<HTMLTextAreaElement>;
  onInput?: React.FormEventHandler<HTMLTextAreaElement>;
}) {
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

  // Re-measure after every render so controlled value changes (not just user
  // input) resize too. No-op on browsers with native field-sizing support.
  React.useLayoutEffect(() => {
    if (supportsFieldSizing || !innerRef.current) return;
    resizeToContent(innerRef.current);
  });

  return {
    ref: (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    onInput: supportsFieldSizing
      ? onInput
      : (event: React.FormEvent<HTMLTextAreaElement>) => {
          resizeToContent(event.currentTarget);
          onInput?.(event);
        },
  };
}

function Textarea({
  className,
  ref,
  onInput,
  ...props
}: React.ComponentProps<"textarea">) {
  const autoGrow = useAutoGrowTextarea({ ref, onInput });

  return (
    <textarea
      data-slot="textarea"
      className={cn(
        "border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16 w-full rounded-md border bg-transparent px-3 py-2 text-base inset-shadow-input-rest transition-[color,box-shadow,border-color] duration-150 ease-out outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className,
      )}
      {...props}
      {...autoGrow}
    />
  );
}

export { Textarea };
